import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from './contexts/AuthContext';
import { getBaseRoute } from './utilities/utility';

import Loading from './components/Loading';
import ProtectedRoute from './components/ProtectedRoute';
import ConfirmToast from './components/modals/ConfirmToast';
import BaseLayout from './layouts/BaseLayout';
import NotFoundPage from './pages/NotFoundPage';

const LoginPage = lazy(() =>
  /* webpackChunkName: "LoginPage" */ import('./pages/LoginPage')
);

const LocalLoginPage = lazy(() =>
  /* webpackChunkName: "LocalLoginPage" */ import('./pages/LocalLoginPage')
);

const ExternalLoginPage = lazy(() =>
  /* webpackChunkName: "ExternalLoginPage" */ import('./pages/ExternalLoginPage')
);

const ActivatePage = lazy(() =>
  /* webpackChunkName: "ActivatePage" */ import('./pages/ActivatePage')
);

const ForgotPasswordPage = lazy(() =>
  /* webpackChunkName: "ForgotPasswordPage" */ import('./pages/ForgotPasswordPage')
);

const ResetPasswordPage = lazy(() =>
  /* webpackChunkName: "ResetPasswordPage" */ import('./pages/ResetPasswordPage')
);

const ErrorPage = lazy(() =>
  import(/* webpackChunkName: "ErrorPage" */ './pages/ErrorPage')
);

const TenantListPage = lazy(() =>
  import(/* webpackChunkName: "TenantListPage" */ './pages/admin/TenantListPage')
);

const TenantUsersPage = lazy(() =>
  import(/* webpackChunkName: "TenantUsersPage" */ './pages/tenant/TenantUsersPage')
);

const GrantTypesPage = lazy(() =>
  import(/* webpackChunkName: "GrantTypesPage" */ './pages/tenant/GrantTypesPage')
);

const FormTemplatesPage = lazy(() =>
  import(/* webpackChunkName: "FormTemplatesPage" */ './pages/tenant/FormTemplatesPage')
);

const GrantRoundsPage = lazy(() =>
  import(/* webpackChunkName: "GrantRoundsPage" */ './pages/tenant/GrantRoundsPage')
);

const ApplicationsPage = lazy(() =>
  import(/* webpackChunkName: "ApplicationsPage" */ './pages/tenant/ApplicationsPage')
);

const ApplicationDetailPage = lazy(() =>
  import(/* webpackChunkName: "ApplicationDetailPage" */ './pages/tenant/ApplicationDetailPage')
);

const TenantDashboardPage = lazy(() =>
  import(/* webpackChunkName: "TenantDashboardPage" */ './pages/tenant/TenantDashboardPage')
);

const ReportsPage = lazy(() =>
  import(/* webpackChunkName: "ReportsPage" */ './pages/tenant/ReportsPage')
);

const SuomifiDemoLoginPage = lazy(() =>
  import(/* webpackChunkName: "SuomifiDemoLoginPage" */ './pages/SuomifiDemoLoginPage')
);

const SuomifiOrganizationSelectPage = lazy(() =>
  import(/* webpackChunkName: "SuomifiOrganizationSelectPage" */ './pages/applicant/SuomifiOrganizationSelectPage')
);

const ApplyHomePage = lazy(() =>
  import(/* webpackChunkName: "ApplyHomePage" */ './pages/applicant/ApplyHomePage')
);

const ApplicantPortalPage = lazy(() =>
  import(/* webpackChunkName: "ApplicantPortalPage" */ './pages/applicant/ApplicantPortalPage')
);

const MyApplicationsPage = lazy(() =>
  import(/* webpackChunkName: "MyApplicationsPage" */ './pages/applicant/MyApplicationsPage')
);

const ApplicationFormPage = lazy(() =>
  import(/* webpackChunkName: "ApplicationFormPage" */ './pages/applicant/ApplicationFormPage')
);

const SurveysPage = lazy(() =>
  import(/* webpackChunkName: "SurveysPage" */ './pages/tenant/SurveysPage')
);

const MessageTemplatesPage = lazy(() =>
  import(/* webpackChunkName: "MessageTemplatesPage" */ './pages/tenant/MessageTemplatesPage')
);

const DecisionTemplatesPage = lazy(() =>
  import(/* webpackChunkName: "DecisionTemplatesPage" */ './pages/tenant/DecisionTemplatesPage')
);

const SurveySubmissionPage = lazy(() =>
  import(/* webpackChunkName: "SurveySubmissionPage" */ './pages/tenant/SurveySubmissionPage')
);

const ApplicantSurveysListPage = lazy(() =>
  import(/* webpackChunkName: "ApplicantSurveysListPage" */ './pages/applicant/ApplicantSurveysListPage')
);

const ApplicantSurveyPage = lazy(() =>
  import(/* webpackChunkName: "ApplicantSurveyPage" */ './pages/applicant/ApplicantSurveyPage')
);

const HelpPage = lazy(() =>
  import(/* webpackChunkName: "HelpPage" */ './pages/HelpPage')
);

const HelpAdminPage = lazy(() =>
  import(/* webpackChunkName: "HelpAdminPage" */ './pages/admin/HelpAdminPage')
);

const ExportApiKeysPage = lazy(() =>
  import(/* webpackChunkName: "ExportApiKeysPage" */ './pages/admin/ExportApiKeysPage')
);

const ArchiveConfigPage = lazy(() =>
  import(/* webpackChunkName: "ArchiveConfigPage" */ './pages/tenant/ArchiveConfigPage')
);

const BrandingPage = lazy(() =>
  import(/* webpackChunkName: "BrandingPage" */ './pages/tenant/BrandingPage')
);

const ApplicantsPage = lazy(() =>
  import(/* webpackChunkName: "ApplicantsPage" */ './pages/tenant/ApplicantsPage')
);

const ApplicantProfilePage = lazy(() =>
  import(/* webpackChunkName: "ApplicantProfilePage" */ './pages/tenant/ApplicantProfilePage')
);

const PersonProfilePage = lazy(() =>
  import(/* webpackChunkName: "PersonProfilePage" */ './pages/tenant/PersonProfilePage')
);

const YearCalendarPage = lazy(() =>
  import(/* webpackChunkName: "YearCalendarPage" */ './pages/tenant/YearCalendarPage')
);
const AuditLogPage = lazy(() =>
  import(/* webpackChunkName: "AuditLogPage" */ './pages/tenant/AuditLogPage')
);

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate replace to={getBaseRoute(user) || '/apply'} />;
}

export default function App() {
  return (
    <>
      <ConfirmToast />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route element={<BaseLayout />}>
            <Route path='/login' element={<LoginPage />} />
            <Route path='/login/local' element={<LocalLoginPage />} />
            <Route path='/login/external' element={<ExternalLoginPage />} />
            <Route path='/login/suomifi-demo/:slug' element={<SuomifiDemoLoginPage />} />
            <Route path='/suomifi/valtuudet' element={<SuomifiOrganizationSelectPage />} />
            <Route path='/activate' element={<ActivatePage />} />
            <Route path='/forgot-password' element={<ForgotPasswordPage />} />
            <Route path='/reset-password' element={<ResetPasswordPage />} />
            <Route path='/error' element={<ErrorPage />} />

            <Route element={<ProtectedRoute redirectPath='/login' loginPath='/login' />}>
              <Route path='/help' element={<HelpPage />} />

              <Route element={<ProtectedRoute roles={['root']} redirectPath='/' />}>
                <Route path='/admin/tenants' element={<TenantListPage />} />
                <Route path='/admin/help' element={<HelpAdminPage />} />
                <Route path='/admin/export-api-keys' element={<ExportApiKeysPage />} />
              </Route>

              <Route
                element={
                  <ProtectedRoute roles={['ylläpitäjä', 'root']} redirectPath='/' />
                }>
                <Route path='/tenants/:tenantId/users' element={<TenantUsersPage />} />
                <Route path='/tenants/:tenantId/grant-types' element={<GrantTypesPage />} />
                <Route path='/tenants/:tenantId/grant-rounds' element={<GrantRoundsPage />} />
                <Route path='/tenants/:tenantId/form-templates' element={<FormTemplatesPage />} />
                <Route path='/tenants/:tenantId/archive-config' element={<ArchiveConfigPage />} />
                <Route path='/tenants/:tenantId/branding' element={<BrandingPage />} />
              </Route>

              <Route
                element={
                  <ProtectedRoute
                    roles={['ylläpitäjä', 'käsittelijä', 'päättäjä', 'root']}
                    redirectPath='/'
                  />
                }>
                <Route path='/tenants/:tenantId' element={<TenantDashboardPage />} />
                <Route path='/tenants/:tenantId/applications' element={<ApplicationsPage />} />
                <Route
                  path='/tenants/:tenantId/applications/:applicationId'
                  element={<ApplicationDetailPage />}
                />
                <Route path='/tenants/:tenantId/applicants' element={<ApplicantsPage />} />
                <Route
                  path='/tenants/:tenantId/applicants/:applicantId'
                  element={<ApplicantProfilePage />}
                />
                <Route
                  path='/tenants/:tenantId/persons/:globalUserId'
                  element={<PersonProfilePage />}
                />
                <Route path='/tenants/:tenantId/year-calendar' element={<YearCalendarPage />} />
                <Route path='/tenants/:tenantId/audit-log' element={<AuditLogPage />} />
                <Route path='/tenants/:tenantId/reports' element={<ReportsPage />} />
                <Route path='/tenants/:tenantId/surveys' element={<SurveysPage />} />
                <Route path='/tenants/:tenantId/message-templates' element={<MessageTemplatesPage />} />
                <Route path='/tenants/:tenantId/decision-templates' element={<DecisionTemplatesPage />} />
                <Route
                  path='/tenants/:tenantId/surveys/:surveyId/submissions/:submissionId'
                  element={<SurveySubmissionPage />}
                />
              </Route>
            </Route>

            <Route path='/' element={<RootRedirect />} />
            <Route path='/apply' element={<ApplyHomePage />} />

            <Route
              element={
                <ProtectedRoute loginPath='/apply' />
              }>
              <Route path='/apply/:tenantId' element={<ApplicantPortalPage />} />
              <Route path='/apply/:tenantId/applications' element={<MyApplicationsPage />} />
              <Route
                path='/apply/:tenantId/applications/:applicationId'
                element={<ApplicationFormPage />}
              />
              <Route path='/apply/:tenantId/surveys' element={<ApplicantSurveysListPage />} />
              <Route path='/apply/:tenantId/surveys/:submissionId' element={<ApplicantSurveyPage />} />
            </Route>

            <Route path='*' element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
